import { Box, debounce, Skeleton, Stack, Typography } from '@mui/material';

import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useLoadCardCustomAttributeValues } from 'src/components/CustomAttributes/use-load-card-custom-attribute-values';
import { AutocompleteSearchBar } from 'src/components/SearchBar/autocomplete-search-bar';
import { SidePanelCaption } from 'src/components/SidePanelCaption';
import { SideTabStyledWrapper } from 'src/components/SidePanelTabs/SiteTab/site-tab';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import { Client, isClientDeleted } from 'src/graphql';
import { useSearch } from 'src/graphql/entities/search/use-search';
import { ConditionInput, EntityNames } from 'src/graphql/types';
import { SiteCard } from 'src/pages/Conversations/SidePanel/ConversationDetails/ConversationSite/site-card';
import { Modals, useModalMethods } from 'src/store/modals';
import { useInViewTrigger } from 'src/utils/hooks';
import { AppEvents, subscribeToAppEvent } from 'src/utils/hooks/use-app-events';

interface ClientSitesProps {
  client: Client;
}

const AddressItemSkeleton = () => (
  <Stack
    ml={1}
    overflow="hidden"
    flexGrow={1}
    direction="row"
    spacing={1}
    alignItems="center"
    padding="0.8rem"
  >
    <Skeleton variant="circular" width={20} height={20} />
    <Skeleton variant="circular" width={20} height={20} />
    <Skeleton width="70%" />
  </Stack>
);

export const ClientSites = ({ client }: ClientSitesProps) => {
  const [input, setInput] = useState('');

  const { showModal: showUpdateClientToAddressModal } = useModalMethods(
    Modals.UpdateClientToAddress,
  );

  const filters = useMemo<ConditionInput[]>(
    () => [
      {
        attribute: 'clients/clientId',
        operator: 'in',
        value: client.clientId,
      },
    ],
    [client.clientId],
  );

  const {
    records: clientAddresses,
    loadMore,
    hasMore,
    isLoading,
    isLoadingMore,
    cacheManager,
  } = useSearch({
    entityName: EntityNames.Site,
    searchTerm: input,
    showResultsWhenEmptySearchTerm: true,
    filter: filters,
  }) ?? {};

  useLoadCardCustomAttributeValues({
    sectionKey: 'site',
    entity: 'Address',
    entityIds: clientAddresses?.map((site) => site.addressId) ?? [],
  });

  const inViewElementRef = useInViewTrigger({
    onEnter: loadMore,
    enabled: !isLoading && !!hasMore && !isLoadingMore,
  });

  const handleOnChange = useMemo(
    () =>
      debounce(({ target }: ChangeEvent) => {
        const value = (target as HTMLInputElement).value;
        setInput(value);
      }, SEARCH_DEBOUNCE_DELAY),
    [],
  );

  useEffect(() => {
    return subscribeToAppEvent(
      AppEvents.ClientsToAddressAssociation,
      ({ address, clientIds }) => {
        if (!clientIds.includes(client.clientId)) return;
        cacheManager?.update?.((cache) => {
          cache.getAddress.records.push(address as (typeof cache.getAddress.records)[0]);
        });
      },
    );
  }, [cacheManager, client.clientId]);

  const onAddButtonClick = useCallback(() => {
    showUpdateClientToAddressModal({
      client,
    });
  }, [client, showUpdateClientToAddressModal]);

  return (
    <SideTabStyledWrapper>
      <SidePanelCaption py={1}>Associated Sites</SidePanelCaption>
      <AutocompleteSearchBar
        disabled={isClientDeleted(client)}
        addButtonText="+ Add Site"
        addButtonTestId="client-sites-add-site"
        onAddButtonClick={onAddButtonClick}
        handleOnFilterResultsChange={handleOnChange}
      />

      <Stack display="grid" gap={3}>
        {!!isLoading && !clientAddresses?.length && <AddressItemSkeleton />}
        {clientAddresses?.map((clientAddress) => (
          <SiteCard key={clientAddress.addressId} address={clientAddress} client={client} />
        ))}
        {!!isLoadingMore && <AddressItemSkeleton />}
        <Box ref={inViewElementRef} sx={{ height: 2 }} />
        {!isLoading && !clientAddresses?.length && (
          <Stack padding="0.5rem">
            <Typography alignSelf="center" variant="body2">
              No results
            </Typography>
          </Stack>
        )}
      </Stack>
    </SideTabStyledWrapper>
  );
};
